/**
 * Stable machine-readable codes the API writes into
 * `LeaderboardRecord.disqualifiedReason` when a user is flagged out of
 * the rewards pool. Declared as `as const` so the union of valid codes
 * is exported as a type and used to enforce exhaustiveness at every
 * consumer (front-end label maps, audit dashboards, etc.).
 *
 * Adding a new reason:
 *   1. Append a new entry here.
 *   2. TypeScript will then fail on every `Record<DisqualifiedReasonCode, …>`
 *      consumer until they add a mapping for the new code — that's the
 *      whole point of routing the type through this single declaration.
 */
export const DISQUALIFIED_REASONS = {
  EXTERNAL_ORDERS_DETECTED: "EXTERNAL_ORDERS_DETECTED",
  /** Arena drawdown breach; rendered as "Eliminated", not a fraud disqualification. */
  DRAWDOWN_ELIMINATED: "DRAWDOWN_ELIMINATED",
  /** The user gave up. Kept apart from DRAWDOWN_ELIMINATED so forfeits stay visible in the data. */
  FORFEITED: "FORFEITED",
  /** Lowest PnL at a Battle Royale elimination; rendered as "Eliminated", like DRAWDOWN_ELIMINATED. */
  BATTLE_ROYALE_ELIMINATED: "BATTLE_ROYALE_ELIMINATED",
} as const;

export type DisqualifiedReasonCode = (typeof DISQUALIFIED_REASONS)[keyof typeof DISQUALIFIED_REASONS];
